import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  AuthResponse, 
  LoginData, 
  RegisterData, 
  ApiResponse, 
  DashboardConfig,
  DashboardTheme,
  User,
  Group,
  Tile,
  Tab,
  SearchResult,
  CreateTileRequest,
  CreateGroupRequest,
  CreateTabRequest,
  UpdateTileRequest,
  UpdateGroupRequest,
  UpdateTabRequest,
  ReorderRequest,
  MoveTileRequest,
  ThemePreset
} from '../types';

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 10000,
  withCredentials: true, // Important for sending cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add timestamp to prevent caching
    config.headers['X-Requested-At'] = new Date().toISOString();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for global error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // Handle common error scenarios
    if (error.response?.status === 401) {
      // Only redirect to login if we're not already on login/register pages
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
        localStorage.removeItem('isAuthenticated');
        window.location.href = '/login';
      }
    } else if (error.response?.status >= 500) {
      // Server error
      console.error('Server error:', error.response?.data?.message || 'Internal server error');
    }
    
    return Promise.reject(error);
  }
);

// Auth API functions
export const authApi = {
  // Register new user
  register: async (userData: RegisterData): Promise<AuthResponse> => {
    try {
      const response = await api.post<AuthResponse>('/auth/register', userData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  },

  // Login user
  login: async (credentials: LoginData): Promise<AuthResponse> => {
    try {
      const response = await api.post<AuthResponse>('/auth/login', credentials);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  },

  // Logout user
  logout: async (): Promise<ApiResponse> => {
    try {
      const response = await api.post<ApiResponse>('/auth/logout');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Logout failed');
    }
  },

  // Verify token
  verify: async (): Promise<{ user: User }> => {
    try {
      const response = await api.get<ApiResponse<{ user: User }>>('/auth/verify');
      return response.data.data!;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Token verification failed');
    }
  },

  // Get user profile
  getProfile: async (): Promise<{ user: User }> => {
    try {
      const response = await api.get<ApiResponse<{ user: User }>>('/auth/profile');
      return response.data.data!;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get profile');
    }
  },

  // Change password
  changePassword: async (currentPassword: string, newPassword: string): Promise<ApiResponse> => {
    try {
      const response = await api.post<ApiResponse>('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Password change failed');
    }
  }
};

// Dashboard API functions
export const dashboardApi = {
  // Configuration operations
  getConfig: async (): Promise<DashboardConfig> => {
    try {
      const response = await api.get<ApiResponse<{ config: DashboardConfig }>>('/dashboard/config');
      return response.data.data!.config;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get dashboard config');
    }
  },

  updateConfig: async (config: DashboardConfig): Promise<ApiResponse> => {
    try {
      const response = await api.put<ApiResponse>('/dashboard/config', { config });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update dashboard config');
    }
  },

  resetConfig: async (): Promise<DashboardConfig> => {
    try {
      const response = await api.post<ApiResponse<{ config: DashboardConfig }>>('/dashboard/config/reset');
      return response.data.data!.config;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to reset dashboard config');
    }
  },

  // Group operations
  createGroup: async (groupData: CreateGroupRequest): Promise<Group> => {
    try {
      const response = await api.post<ApiResponse<{ group: Group }>>('/dashboard/groups', groupData);
      return response.data.data!.group;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create group');
    }
  },

  updateGroup: async (id: string, updates: UpdateGroupRequest): Promise<Group> => {
    try {
      const response = await api.put<ApiResponse<{ group: Group }>>(`/dashboard/groups/${id}`, updates);
      return response.data.data!.group;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update group');
    }
  },

  deleteGroup: async (id: string): Promise<void> => {
    try {
      await api.delete<ApiResponse>(`/dashboard/groups/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete group');
    }
  },

  reorderGroups: async (groupIds: string[]): Promise<void> => {
    try {
      await api.patch<ApiResponse>('/dashboard/groups/reorder', { groupIds });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to reorder groups');
    }
  },

  // Tile operations
  createTile: async (groupId: string, tileData: CreateTileRequest): Promise<Tile> => {
    try {
      const response = await api.post<ApiResponse<{ tile: Tile }>>(`/dashboard/groups/${groupId}/tiles`, tileData);
      return response.data.data!.tile;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create tile');
    }
  },

  updateTile: async (id: string, updates: UpdateTileRequest): Promise<void> => {
    try {
      await api.put<ApiResponse>(`/dashboard/tiles/${id}`, updates);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update tile');
    }
  },

  deleteTile: async (id: string): Promise<void> => {
    try {
      await api.delete<ApiResponse>(`/dashboard/tiles/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete tile');
    }
  },

  reorderTiles: async (groupId: string, tileIds: string[]): Promise<void> => {
    try {
      await api.patch<ApiResponse>('/dashboard/tiles/reorder', { groupId, tileIds });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to reorder tiles');
    }
  },

  moveTile: async (tileId: string, moveData: MoveTileRequest): Promise<void> => {
    try {
      await api.patch<ApiResponse>(`/dashboard/tiles/${tileId}/move`, moveData);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to move tile');
    }
  },

  // Search operations
  searchTiles: async (query: string): Promise<SearchResult[]> => {
    try {
      const response = await api.get<ApiResponse<{ results: SearchResult[] }>>(`/dashboard/search?q=${encodeURIComponent(query)}`);
      return response.data.data!.results;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Search failed');
    }
  },

  // Theme operations
  updateTheme: async (theme: Partial<DashboardTheme>): Promise<DashboardTheme> => {
    try {
      const response = await api.put<ApiResponse<{ theme: DashboardTheme }>>('/dashboard/theme', { theme });
      return response.data.data!.theme;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update theme');
    }
  },

  getThemePresets: async (): Promise<ThemePreset[]> => {
    try {
      const response = await api.get<ApiResponse<{ presets: ThemePreset[] }>>('/dashboard/theme/presets');
      return response.data.data!.presets;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get theme presets');
    }
  },

  // Tab operations
  createTab: async (tabData: CreateTabRequest): Promise<Tab> => {
    try {
      const response = await api.post<ApiResponse<{ tab: Tab }>>('/dashboard/tabs', tabData);
      return response.data.data!.tab;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create tab');
    }
  },

  updateTab: async (id: string, updates: UpdateTabRequest): Promise<Tab> => {
    try {
      const response = await api.put<ApiResponse<{ tab: Tab }>>(`/dashboard/tabs/${id}`, updates);
      return response.data.data!.tab;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update tab');
    }
  },

  deleteTab: async (id: string): Promise<void> => {
    try {
      await api.delete<ApiResponse>(`/dashboard/tabs/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete tab');
    }
  },

  reorderTabs: async (tabIds: string[]): Promise<void> => {
    try {
      await api.patch<ApiResponse>('/dashboard/tabs/reorder', { tabIds });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to reorder tabs');
    }
  },

  setActiveTab: async (tabId: string): Promise<void> => {
    try {
      await api.put<ApiResponse>(`/dashboard/tabs/${tabId}/activate`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to set active tab');
    }
  },

  // Custom theme presets
  saveCustomThemePreset: async (name: string, description?: string): Promise<ThemePreset> => {
    try {
      const response = await api.post<ApiResponse<{ preset: ThemePreset }>>('/dashboard/theme/presets', {
        name,
        description
      });
      return response.data.data!.preset;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to save custom theme preset');
    }
  },

  deleteCustomThemePreset: async (presetId: string): Promise<void> => {
    try {
      await api.delete<ApiResponse>(`/dashboard/theme/presets/${presetId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete custom theme preset');
    }
  },

  // Default theme management (admin/superuser only)
  setDefaultTheme: async (description?: string): Promise<void> => {
    try {
      await api.post<ApiResponse>('/dashboard/theme/default', { description });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to set default theme');
    }
  },

  getDefaultTheme: async (): Promise<any> => {
    try {
      const response = await api.get<ApiResponse>('/dashboard/theme/default');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get default theme');
    }
  },

  // Get admin shared themes (available to all users)
  getAdminSharedThemes: async (): Promise<any[]> => {
    try {
      const response = await api.get<ApiResponse>('/dashboard/theme/admin-shared');
      return response.data.data || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to get admin shared themes');
    }
  },

  // Apply theme preset with proper tracking
  applyThemePreset: async (theme: DashboardTheme, presetName: string): Promise<DashboardTheme> => {
    try {
      const response = await api.post<ApiResponse<{ theme: DashboardTheme }>>('/dashboard/theme/apply-preset', {
        theme,
        presetName
      });
      return response.data.data!.theme;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to apply theme preset');
    }
  }
};

// Health check
export const healthCheck = async (): Promise<boolean> => {
  try {
    const response = await api.get('/health');
    return response.data.success;
  } catch (error) {
    return false;
  }
};

// Utility function to check if error is network related
export const isNetworkError = (error: any): boolean => {
  return !error.response && error.code === 'NETWORK_ERROR';
};

// Utility function to get error message
export const getErrorMessage = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export default api; 